import {
    Box,
    Button,
    Grid,
    MenuItem,
    Select,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
    Modal,
    IconButton,
    Paper,
    TextField,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";

import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { makeGetRequestToUrl } from "../commonFunc.jsx";
import { makePostRequestToUrl } from "../commonFunc.jsx";
import config from "../../config/config.js";
import TokenError from "../tokenError";

const FleetDetails = () => {
    const serverUrl = config.serverUrl;
    const operationsUrl = config.operationsUrl;

    const navigate = useNavigate();

    // -------------------------------------------------------
    // State
    // -------------------------------------------------------

    const [rows, setRows] = useState([]);

    const [cityFilter, setCityFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [search, setSearch] = useState("");

    const [loading, setLoading] = useState(false);

    const [message, setMessage] = useState("");
    const [snackbar, showSnackbar] = useState(false);

    const [tokenExpired, setTokenExpired] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    // Details modal
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedFleet, setSelectedFleet] = useState(null);

    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Sorting
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("id");

    // -------------------------------------------------------
    // Helpers
    // -------------------------------------------------------

    const getStatusLabel = (status) => {
        const value = Number(status);

        if (value === 1) return "Approved";
        if (value === 2) return "Rejected";

        return "Pending";
    };

    const getStatusColor = (status) => {
        const value = Number(status);

        if (value === 1) return "#0DBA78";
        if (value === 2) return "#D14545";

        return "#F59E0B";
    };

    const getCityName = (row) => {
        return (
            row?.city_name ||
            row?.city?.city_name ||
            row?.city ||
            row?.city_id ||
            "--"
        );
    };

    const getPhone = (row) => {
        return (
            row?.phone_no ||
            row?.phone_number ||
            row?.phone ||
            "--"
        );
    };

    const getFleetId = (row) => {
        return row?.id || row?.fleet_id;
    };

    const getDocuments = (row) => {
        if (Array.isArray(row?.documents)) {
            return row.documents;
        }

        return [];
    };

    // -------------------------------------------------------
    // Fetch fleet data
    // -------------------------------------------------------

    const fetchFleetData = async () => {
        setLoading(true);

        try {
            const token = localStorage.getItem("token");

            const url = serverUrl + "/fleet/get_details";

            const timestamp = new Date().getTime();

            const body = {
                token,
                timestamp,
                fleet_id:4
            };

            const response = await makeGetRequestToUrl(url, body);

            if (response?.flag === 200) {
                setRows(Array.isArray(response.data) ? response.data : []);
                setPage(0);
                return;
            }

            if (response?.flag === 102) {
                localStorage.removeItem("token");

                setErrorMessage(
                    "Your session has expired. Please log in again."
                );

                setTokenExpired(true);

                setTimeout(() => {
                    navigate("/signup");
                }, 2000);

                return;
            }

            setRows([]);

            setMessage(
                response?.message || "Failed to fetch fleet data"
            );

            showSnackbar(true);
        } catch (error) {
            console.error("Error fetching fleet data:", error);

            setMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong while fetching fleet data"
            );

            showSnackbar(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFleetData();
    }, []);

    // -------------------------------------------------------
    // Approve / Reject Fleet
    // -------------------------------------------------------

    const handleApproveReject = async (status, fleetId) => {
        if (!fleetId) {
            setMessage("Fleet ID is missing");
            showSnackbar(true);
            return;
        }

        const token = localStorage.getItem("token");

        const payload = {
            fleet_id: fleetId,
            status: status,
            token: token,
        };

        try {
            const url =
                operationsUrl + "/fleet/approve_reject_fleet";

            const response = await makePostRequestToUrl(
                url,
                payload
            );

            if (response?.flag === 200) {
                setMessage(
                    response?.message ||
                    (
                        status === 1
                            ? "Fleet approved successfully"
                            : "Fleet rejected successfully"
                    )
                );

                showSnackbar(true);

                await fetchFleetData();

                // Update selected fleet if modal is open
                setSelectedFleet((prev) =>
                    prev
                        ? {
                            ...prev,
                            status,
                        }
                        : prev
                );

                return;
            }

            if (response?.flag === 102) {
                localStorage.removeItem("token");

                setErrorMessage(
                    "Your session has expired. Please log in again."
                );

                setTokenExpired(true);

                setTimeout(() => {
                    navigate("/signup");
                }, 2000);

                return;
            }

            setMessage(
                response?.message ||
                "Unable to update fleet status"
            );

            showSnackbar(true);
        } catch (error) {
            console.error(
                "Error while approving/rejecting fleet:",
                error
            );

            setMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong while updating fleet status"
            );

            showSnackbar(true);
        }
    };

    // -------------------------------------------------------
    // View Fleet
    // -------------------------------------------------------

    const handleViewFleet = (row) => {
        setSelectedFleet(row);
        setDetailsOpen(true);
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setSelectedFleet(null);
    };

    // -------------------------------------------------------
    // Search / Filters
    // -------------------------------------------------------

    const cityOptions = useMemo(() => {
        const cities = rows
            .map((row) => getCityName(row))
            .filter(
                (city) =>
                    city !== "--" &&
                    city !== null &&
                    city !== undefined
            );

        return [...new Set(cities)];
    }, [rows]);

    const filteredRows = useMemo(() => {
        return rows.filter((row) => {
            const searchValue = search.trim().toLowerCase();

            const matchesSearch =
                !searchValue ||
                String(row?.name || "")
                    .toLowerCase()
                    .includes(searchValue) ||
                String(row?.email || "")
                    .toLowerCase()
                    .includes(searchValue) ||
                String(getPhone(row))
                    .toLowerCase()
                    .includes(searchValue) ||
                String(getFleetId(row))
                    .toLowerCase()
                    .includes(searchValue);

            const matchesStatus =
                statusFilter === "" ||
                Number(row?.status) === Number(statusFilter);

            const matchesCity =
                cityFilter === "" ||
                String(getCityName(row)) === String(cityFilter);

            return (
                matchesSearch &&
                matchesStatus &&
                matchesCity
            );
        });
    }, [
        rows,
        search,
        statusFilter,
        cityFilter,
    ]);

    // -------------------------------------------------------
    // Sorting
    // -------------------------------------------------------

    const sortedRows = useMemo(() => {
        const result = [...filteredRows];

        result.sort((a, b) => {
            let valueA = a?.[orderBy];
            let valueB = b?.[orderBy];

            if (orderBy === "city") {
                valueA = getCityName(a);
                valueB = getCityName(b);
            }

            if (orderBy === "phone") {
                valueA = getPhone(a);
                valueB = getPhone(b);
            }

            if (valueA === null || valueA === undefined) {
                valueA = "";
            }

            if (valueB === null || valueB === undefined) {
                valueB = "";
            }

            valueA = String(valueA).toLowerCase();
            valueB = String(valueB).toLowerCase();

            if (valueA < valueB) {
                return order === "asc" ? -1 : 1;
            }

            if (valueA > valueB) {
                return order === "asc" ? 1 : -1;
            }

            return 0;
        });

        return result;
    }, [
        filteredRows,
        order,
        orderBy,
    ]);

    const handleRequestSort = (property) => {
        const isAscending =
            orderBy === property && order === "asc";

        setOrder(isAscending ? "desc" : "asc");
        setOrderBy(property);
    };

    // -------------------------------------------------------
    // Pagination
    // -------------------------------------------------------

    const totalEntries = sortedRows.length;

    const totalPages = Math.ceil(
        totalEntries / rowsPerPage
    );

    const paginatedRows = sortedRows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const handleChangePage = (newPage) => {
        if (newPage < 0) return;

        if (totalPages > 0 && newPage >= totalPages) {
            return;
        }

        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(Number(event.target.value));
        setPage(0);
    };

    // -------------------------------------------------------
    // Snackbar
    // -------------------------------------------------------

    const handleSnackbarClose = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }

        showSnackbar(false);
    };

    // -------------------------------------------------------
    // Reset Filters
    // -------------------------------------------------------

    const handleResetFilters = () => {
        setSearch("");
        setCityFilter("");
        setStatusFilter("");
        setPage(0);
    };

    // -------------------------------------------------------
    // Render
    // -------------------------------------------------------

    return (
        <>
            {tokenExpired && <TokenError />}

            <Box sx={{ width: "100%" }}>
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 3,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "28px",
                            fontWeight: 500,
                        }}
                    >
                        Fleet
                    </Typography>

                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={fetchFleetData}
                        disabled={loading}
                        sx={{
                            height: 42,
                            textTransform: "none",
                            borderRadius: "8px",
                        }}
                    >
                        Refresh
                    </Button>
                </Box>

                {/* Filters */}
                <Grid
                    container
                    spacing={2}
                    alignItems="center"
                    sx={{
                        mb: 3,
                    }}
                >
                    {/* Search */}
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            size="small"
                            placeholder="Search fleet, email, phone..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(0);
                            }}
                        />
                    </Grid>

                    {/* City */}
                    <Grid item xs={12} sm={6} md={2.5}>
                        <Select
                            fullWidth
                            size="small"
                            displayEmpty
                            value={cityFilter}
                            onChange={(e) => {
                                setCityFilter(e.target.value);
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">
                                All Cities
                            </MenuItem>

                            {cityOptions.map((city) => (
                                <MenuItem
                                    key={city}
                                    value={city}
                                >
                                    {city}
                                </MenuItem>
                            ))}
                        </Select>
                    </Grid>

                    {/* Status */}
                    <Grid item xs={12} sm={6} md={2.5}>
                        <Select
                            fullWidth
                            size="small"
                            displayEmpty
                            value={statusFilter}
                            onChange={(e) => {
                                setStatusFilter(e.target.value);
                                setPage(0);
                            }}
                        >
                            <MenuItem value="">
                                All Status
                            </MenuItem>

                            <MenuItem value="0">
                                Pending
                            </MenuItem>

                            <MenuItem value="1">
                                Approved
                            </MenuItem>

                            <MenuItem value="2">
                                Rejected
                            </MenuItem>
                        </Select>
                    </Grid>

                    {/* Reset */}
                    <Grid item xs={12} md={2}>
                        <Button
                            variant="outlined"
                            onClick={handleResetFilters}
                            sx={{
                                width: "100%",
                                height: 40,
                                textTransform: "none",
                                borderRadius: "8px",
                            }}
                        >
                            Reset
                        </Button>
                    </Grid>
                </Grid>

                {/* Summary */}
                <Grid
                    container
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Grid item xs={12} sm={4}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: "10px",
                                border: "1px solid #E5E7EB",
                            }}
                        >
                            <Typography
                                sx={{
                                    color: "#6B7280",
                                    fontSize: 13,
                                }}
                            >
                                Total Fleets
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 26,
                                    fontWeight: 600,
                                    mt: 0.5,
                                }}
                            >
                                {rows.length}
                            </Typography>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: "10px",
                                border: "1px solid #E5E7EB",
                            }}
                        >
                            <Typography
                                sx={{
                                    color: "#6B7280",
                                    fontSize: 13,
                                }}
                            >
                                Pending
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 26,
                                    fontWeight: 600,
                                    color: "#F59E0B",
                                    mt: 0.5,
                                }}
                            >
                                {
                                    rows.filter(
                                        (row) =>
                                            Number(row.status) === 0
                                    ).length
                                }
                            </Typography>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 2,
                                borderRadius: "10px",
                                border: "1px solid #E5E7EB",
                            }}
                        >
                            <Typography
                                sx={{
                                    color: "#6B7280",
                                    fontSize: 13,
                                }}
                            >
                                Approved
                            </Typography>

                            <Typography
                                sx={{
                                    fontSize: 26,
                                    fontWeight: 600,
                                    color: "#0DBA78",
                                    mt: 0.5,
                                }}
                            >
                                {
                                    rows.filter(
                                        (row) =>
                                            Number(row.status) === 1
                                    ).length
                                }
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Table */}
                <TableContainer
                    sx={{
                        maxHeight: "70vh",
                        border: "1px solid #E5E7EB",
                        borderRadius: "10px",
                        backgroundColor: "#fff",
                    }}
                >
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">
                                    <TableSortLabel
                                        active={orderBy === "id"}
                                        direction={
                                            orderBy === "id"
                                                ? order
                                                : "asc"
                                        }
                                        onClick={() =>
                                            handleRequestSort("id")
                                        }
                                    >
                                        ID
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell align="center">
                                    <TableSortLabel
                                        active={
                                            orderBy === "name"
                                        }
                                        direction={
                                            orderBy === "name"
                                                ? order
                                                : "asc"
                                        }
                                        onClick={() =>
                                            handleRequestSort(
                                                "name"
                                            )
                                        }
                                    >
                                        Fleet Name
                                    </TableSortLabel>
                                </TableCell>

                                <TableCell align="center">
                                    Email
                                </TableCell>

                                <TableCell align="center">
                                    Phone
                                </TableCell>

                                <TableCell align="center">
                                    City
                                </TableCell>

                                <TableCell align="center">
                                    Documents
                                </TableCell>

                                <TableCell align="center">
                                    Status
                                </TableCell>

                                <TableCell align="center">
                                    Action
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={8}
                                        align="center"
                                        sx={{ py: 5 }}
                                    >
                                        <Typography>
                                            Loading fleets...
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : paginatedRows.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={8}
                                        align="center"
                                        sx={{ py: 5 }}
                                    >
                                        <Typography
                                            sx={{
                                                color: "#6B7280",
                                            }}
                                        >
                                            No fleet found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedRows.map((row) => {
                                    const fleetId =
                                        getFleetId(row);

                                    const documents =
                                        getDocuments(row);

                                    const status =
                                        Number(row.status);

                                    return (
                                        <TableRow
                                            key={fleetId}
                                            hover
                                        >
                                            {/* ID */}
                                            <TableCell align="center">
                                                <Typography
                                                    sx={{
                                                        minWidth: 70,
                                                    }}
                                                >
                                                    {fleetId ||
                                                        "--"}
                                                </Typography>
                                            </TableCell>

                                            {/* Fleet name */}
                                            <TableCell align="center">
                                                <Typography
                                                    sx={{
                                                        minWidth: 150,
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    {row?.name ||
                                                        "--"}
                                                </Typography>
                                            </TableCell>

                                            {/* Email */}
                                            <TableCell align="center">
                                                <Typography
                                                    sx={{
                                                        minWidth: 180,
                                                    }}
                                                >
                                                    {row?.email ||
                                                        "--"}
                                                </Typography>
                                            </TableCell>

                                            {/* Phone */}
                                            <TableCell align="center">
                                                <Typography
                                                    sx={{
                                                        minWidth: 130,
                                                    }}
                                                >
                                                    {getPhone(row)}
                                                </Typography>
                                            </TableCell>

                                            {/* City */}
                                            <TableCell align="center">
                                                <Typography
                                                    sx={{
                                                        minWidth: 120,
                                                    }}
                                                >
                                                    {getCityName(
                                                        row
                                                    )}
                                                </Typography>
                                            </TableCell>

                                            {/* Documents */}
                                            <TableCell align="center">
                                                <Button
                                                    size="small"
                                                    startIcon={
                                                        <VisibilityIcon />
                                                    }
                                                    onClick={() =>
                                                        handleViewFleet(
                                                            row
                                                        )
                                                    }
                                                    sx={{
                                                        textTransform:
                                                            "none",
                                                    }}
                                                >
                                                    {documents.length}{" "}
                                                    Documents
                                                </Button>
                                            </TableCell>

                                            {/* Status */}
                                            <TableCell align="center">
                                                <Typography
                                                    sx={{
                                                        minWidth: 100,
                                                        color: getStatusColor(
                                                            status
                                                        ),
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    {getStatusLabel(
                                                        status
                                                    )}
                                                </Typography>
                                            </TableCell>

                                            {/* Actions */}
                                            <TableCell align="center">
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        flexDirection:
                                                            "column",
                                                        gap: 1,
                                                        alignItems:
                                                            "center",
                                                    }}
                                                >
                                                    {/* View */}
                                                    <Button
                                                        variant="outlined"
                                                        startIcon={
                                                            <VisibilityIcon />
                                                        }
                                                        onClick={() =>
                                                            handleViewFleet(
                                                                row
                                                            )
                                                        }
                                                        sx={{
                                                            width: 190,
                                                            height: 38,
                                                            textTransform:
                                                                "none",
                                                            borderRadius:
                                                                "8px",
                                                            color: "#0065FF",
                                                            borderColor:
                                                                "#0065FF",
                                                        }}
                                                    >
                                                        View Details
                                                    </Button>

                                                    {/* Approve / Reject */}
                                                    <Box
                                                        sx={{
                                                            display:
                                                                "flex",
                                                            width: 190,
                                                            border:
                                                                "1px solid #E0E0E0",
                                                            borderRadius:
                                                                "8px",
                                                            overflow:
                                                                "hidden",
                                                        }}
                                                    >
                                                        {/* Approve */}

                                                        {(status ===
                                                            0 ||
                                                            status ===
                                                                2) && (
                                                            <Button
                                                                variant="text"
                                                                startIcon={
                                                                    <CheckCircleOutlineIcon />
                                                                }
                                                                onClick={() =>
                                                                    handleApproveReject(
                                                                        1,
                                                                        fleetId
                                                                    )
                                                                }
                                                                sx={{
                                                                    flex: 1,
                                                                    height: 38,
                                                                    textTransform:
                                                                        "none",
                                                                    fontWeight: 600,
                                                                    borderRadius: 0,
                                                                    color:
                                                                        status ===
                                                                        1
                                                                            ? "#fff"
                                                                            : "#0DBA78",
                                                                    backgroundColor:
                                                                        status ===
                                                                        1
                                                                            ? "#0DBA78"
                                                                            : "transparent",
                                                                    "&:hover":
                                                                        {
                                                                            backgroundColor:
                                                                                "#EFFBF7",
                                                                        },
                                                                }}
                                                            >
                                                                Approve
                                                            </Button>
                                                        )}

                                                        {(status ===
                                                            0 ||
                                                            status ===
                                                                1) && (
                                                            <>
                                                                <Box
                                                                    sx={{
                                                                        width: "1px",
                                                                        backgroundColor:
                                                                            "#E0E0E0",
                                                                    }}
                                                                />

                                                                {/* Reject */}

                                                                <Button
                                                                    variant="text"
                                                                    startIcon={
                                                                        <CancelOutlinedIcon />
                                                                    }
                                                                    onClick={() =>
                                                                        handleApproveReject(
                                                                            2,
                                                                            fleetId
                                                                        )
                                                                    }
                                                                    sx={{
                                                                        flex: 1,
                                                                        height: 38,
                                                                        textTransform:
                                                                            "none",
                                                                        fontWeight: 600,
                                                                        borderRadius: 0,
                                                                        color:
                                                                            status ===
                                                                            2
                                                                                ? "#fff"
                                                                                : "#D14545",
                                                                        backgroundColor:
                                                                            status ===
                                                                            2
                                                                                ? "#D14545"
                                                                                : "transparent",
                                                                        "&:hover":
                                                                            {
                                                                                backgroundColor:
                                                                                    "#FBEEEE",
                                                                            },
                                                                    }}
                                                                >
                                                                    Reject
                                                                </Button>
                                                            </>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mt: 2,
                        flexWrap: "wrap",
                        gap: 2,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: 13,
                            color: "#6B7280",
                        }}
                    >
                        {totalEntries === 0
                            ? "Showing 0 entries"
                            : `Showing ${
                                  page * rowsPerPage + 1
                              } to ${Math.min(
                                  (page + 1) *
                                      rowsPerPage,
                                  totalEntries
                              )} of ${totalEntries} entries`}
                    </Typography>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <Select
                            size="small"
                            value={rowsPerPage}
                            onChange={
                                handleRowsPerPageChange
                            }
                        >
                            <MenuItem value={10}>
                                10
                            </MenuItem>
                            <MenuItem value={25}>
                                25
                            </MenuItem>
                            <MenuItem value={50}>
                                50
                            </MenuItem>
                        </Select>

                        <Button
                            disabled={page === 0}
                            onClick={() =>
                                handleChangePage(
                                    page - 1
                                )
                            }
                            sx={{
                                minWidth: 40,
                            }}
                        >
                            Previous
                        </Button>

                        <Typography
                            sx={{
                                minWidth: 60,
                                textAlign: "center",
                                fontSize: 14,
                            }}
                        >
                            {totalPages === 0
                                ? 0
                                : page + 1}{" "}
                            / {totalPages || 0}
                        </Typography>

                        <Button
                            disabled={
                                totalPages === 0 ||
                                page >= totalPages - 1
                            }
                            onClick={() =>
                                handleChangePage(
                                    page + 1
                                )
                            }
                            sx={{
                                minWidth: 40,
                            }}
                        >
                            Next
                        </Button>
                    </Box>
                </Box>
            </Box>

            {/* ---------------------------------------------------
                Fleet Details Modal
            --------------------------------------------------- */}

            <Modal
                open={detailsOpen}
                onClose={handleCloseDetails}
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform:
                            "translate(-50%, -50%)",
                        width: {
                            xs: "95%",
                            md: "700px",
                        },
                        maxHeight: "90vh",
                        overflowY: "auto",
                        bgcolor: "#fff",
                        borderRadius: "12px",
                        boxShadow: 24,
                        outline: "none",
                    }}
                >
                    {/* Modal Header */}

                    <Box
                        sx={{
                            display: "flex",
                            justifyContent:
                                "space-between",
                            alignItems: "center",
                            px: 3,
                            py: 2,
                            borderBottom:
                                "1px solid #E5E7EB",
                        }}
                    >
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 20,
                                    fontWeight: 600,
                                }}
                            >
                                Fleet Details
                            </Typography>

                            {selectedFleet && (
                                <Typography
                                    sx={{
                                        fontSize: 13,
                                        color: "#6B7280",
                                        mt: 0.5,
                                    }}
                                >
                                    Fleet ID:{" "}
                                    {getFleetId(
                                        selectedFleet
                                    )}
                                </Typography>
                            )}
                        </Box>

                        <IconButton
                            onClick={
                                handleCloseDetails
                            }
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {selectedFleet && (
                        <Box sx={{ p: 3 }}>
                            {/* Basic Details */}

                            <Paper
                                elevation={0}
                                sx={{
                                    border:
                                        "1px solid #E5E7EB",
                                    borderRadius:
                                        "10px",
                                    p: 2.5,
                                    mb: 2,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 17,
                                        fontWeight: 600,
                                        mb: 2,
                                    }}
                                >
                                    Fleet Information
                                </Typography>

                                <Grid
                                    container
                                    spacing={2}
                                >
                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                color: "#6B7280",
                                            }}
                                        >
                                            Fleet Name
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: 15,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {selectedFleet.name ||
                                                "--"}
                                        </Typography>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                color: "#6B7280",
                                            }}
                                        >
                                            Email
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: 15,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {selectedFleet.email ||
                                                "--"}
                                        </Typography>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                color: "#6B7280",
                                            }}
                                        >
                                            Phone
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: 15,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {getPhone(
                                                selectedFleet
                                            )}
                                        </Typography>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                        sm={6}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                color: "#6B7280",
                                            }}
                                        >
                                            City
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: 15,
                                                fontWeight: 500,
                                            }}
                                        >
                                            {getCityName(
                                                selectedFleet
                                            )}
                                        </Typography>
                                    </Grid>

                                    <Grid
                                        item
                                        xs={12}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: 12,
                                                color: "#6B7280",
                                            }}
                                        >
                                            Status
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: 15,
                                                fontWeight: 600,
                                                color: getStatusColor(
                                                    selectedFleet.status
                                                ),
                                            }}
                                        >
                                            {getStatusLabel(
                                                selectedFleet.status
                                            )}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>

                            {/* Documents */}

                            <Paper
                                elevation={0}
                                sx={{
                                    border:
                                        "1px solid #E5E7EB",
                                    borderRadius:
                                        "10px",
                                    p: 2.5,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: 17,
                                        fontWeight: 600,
                                        mb: 2,
                                    }}
                                >
                                    Documents
                                </Typography>

                                {getDocuments(
                                    selectedFleet
                                ).length === 0 ? (
                                    <Typography
                                        sx={{
                                            color: "#6B7280",
                                            fontSize: 14,
                                        }}
                                    >
                                        No documents
                                        available
                                    </Typography>
                                ) : (
                                    getDocuments(
                                        selectedFleet
                                    ).map(
                                        (
                                            document,
                                            index
                                        ) => (
                                            <Box
                                                key={
                                                    document?.document_id ||
                                                    index
                                                }
                                                sx={{
                                                    border:
                                                        "1px solid #E5E7EB",
                                                    borderRadius:
                                                        "8px",
                                                    p: 2,
                                                    mb: 1.5,
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display:
                                                            "flex",
                                                        justifyContent:
                                                            "space-between",
                                                        alignItems:
                                                            "center",
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontWeight: 600,
                                                            fontSize: 14,
                                                        }}
                                                    >
                                                        {document?.document_name ||
                                                            document?.name ||
                                                            `Document ${
                                                                index +
                                                                1
                                                            }`}
                                                    </Typography>

                                                    {document?.document_number && (
                                                        <Typography
                                                            sx={{
                                                                fontSize: 12,
                                                                color: "#6B7280",
                                                            }}
                                                        >
                                                            {
                                                                document.document_number
                                                            }
                                                        </Typography>
                                                    )}
                                                </Box>

                                                {document?.expiry_date && (
                                                    <Typography
                                                        sx={{
                                                            fontSize: 12,
                                                            color: "#6B7280",
                                                            mt: 0.5,
                                                        }}
                                                    >
                                                        Expiry:{" "}
                                                        {
                                                            document.expiry_date
                                                        }
                                                    </Typography>
                                                )}

                                                {/* Images */}

                                                {Array.isArray(
                                                    document?.images
                                                ) &&
                                                    document
                                                        .images
                                                        .length >
                                                        0 && (
                                                        <Box
                                                            sx={{
                                                                display:
                                                                    "grid",
                                                                gridTemplateColumns:
                                                                    {
                                                                        xs: "1fr 1fr",
                                                                        sm: "repeat(3, 1fr)",
                                                                    },
                                                                gap: 1,
                                                                mt: 1.5,
                                                            }}
                                                        >
                                                            {document.images.map(
                                                                (
                                                                    image,
                                                                    imageIndex
                                                                ) => (
                                                                    <Box
                                                                        key={
                                                                            imageIndex
                                                                        }
                                                                        component="img"
                                                                        src={
                                                                            typeof image ===
                                                                            "string"
                                                                                ? image
                                                                                : image?.url ||
                                                                                  image?.src
                                                                        }
                                                                        alt={`Document ${
                                                                            index +
                                                                            1
                                                                        }`}
                                                                        sx={{
                                                                            width: "100%",
                                                                            height: 120,
                                                                            objectFit:
                                                                                "cover",
                                                                            borderRadius:
                                                                                "8px",
                                                                            border:
                                                                                "1px solid #E5E7EB",
                                                                        }}
                                                                    />
                                                                )
                                                            )}
                                                        </Box>
                                                    )}
                                            </Box>
                                        )
                                    )
                                )}
                            </Paper>

                            {/* Modal Actions */}

                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent:
                                        "flex-end",
                                    gap: 1.5,
                                    mt: 3,
                                }}
                            >
                                {Number(
                                    selectedFleet.status
                                ) !== 1 && (
                                    <Button
                                        variant="contained"
                                        startIcon={
                                            <CheckCircleOutlineIcon />
                                        }
                                        onClick={() =>
                                            handleApproveReject(
                                                1,
                                                getFleetId(
                                                    selectedFleet
                                                )
                                            )
                                        }
                                        sx={{
                                            backgroundColor:
                                                "#0DBA78",
                                            textTransform:
                                                "none",
                                            borderRadius:
                                                "8px",
                                            "&:hover": {
                                                backgroundColor:
                                                    "#0BA96C",
                                            },
                                        }}
                                    >
                                        Approve
                                    </Button>
                                )}

                                {Number(
                                    selectedFleet.status
                                ) !== 2 && (
                                    <Button
                                        variant="contained"
                                        startIcon={
                                            <CancelOutlinedIcon />
                                        }
                                        onClick={() =>
                                            handleApproveReject(
                                                2,
                                                getFleetId(
                                                    selectedFleet
                                                )
                                            )
                                        }
                                        sx={{
                                            backgroundColor:
                                                "#D14545",
                                            textTransform:
                                                "none",
                                            borderRadius:
                                                "8px",
                                            "&:hover": {
                                                backgroundColor:
                                                    "#C13C3C",
                                            },
                                        }}
                                    >
                                        Reject
                                    </Button>
                                )}
                            </Box>
                        </Box>
                    )}
                </Box>
            </Modal>

            {/* Snackbar */}

            <Snackbar
                open={snackbar}
                autoHideDuration={2500}
                onClose={handleSnackbarClose}
                message={message}
            />
        </>
    );
};

export default FleetDetails;